import React, { useEffect, useState } from 'react';
import { Table, Tag, Space, Button, Typography, Popconfirm, message, Input, Modal } from 'antd';
import dayjs from 'dayjs';
import request from '@/utils/request';

interface RecordRow {
  orderIndex: number;
  status: string;
  errorMessage: string | null;
}

interface Row {
  id: number;
  bundleId: number;
  bundleTitle: string;
  status: string;
  purchaseCoins: number;
  refundedCoins: number;
  errorMessage: string | null;
  createdAt: string;
  records: RecordRow[];
  userNickname: string | null;
  userPhone: string | null;
}

const PurchaseExceptionsPage: React.FC = () => {
  const [items, setItems] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [noteOpen, setNoteOpen] = useState<Row | null>(null);
  const [note, setNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await request.get<{ items: Row[]; total: number }>('/admin/purchases/exceptions', {
        params: { page, pageSize },
      });
      const data = res.data as unknown as { items: Row[]; total: number };
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const refund = async () => {
    if (!noteOpen) return;
    try {
      await request.post(`/admin/purchases/${noteOpen.id}/refund`, { note });
      message.success('已退款');
      setNoteOpen(null);
      setNote('');
      load();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    }
  };

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        异常订单 / 退款处理
      </Typography.Title>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={items}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: (p, s) => {
            setPage(p);
            setPageSize(s);
          },
        }}
        expandable={{
          expandedRowRender: (row) => (
            <Table
              rowKey="orderIndex"
              dataSource={row.records}
              pagination={false}
              columns={[
                { title: '#', dataIndex: 'orderIndex', width: 60 },
                {
                  title: '状态',
                  dataIndex: 'status',
                  render: (s) => (
                    <Tag color={s === 'success' ? 'green' : s === 'failed' ? 'red' : 'default'}>
                      {s}
                    </Tag>
                  ),
                },
                { title: '错误信息', dataIndex: 'errorMessage', render: (v) => v || '-' },
              ]}
            />
          ),
        }}
        columns={[
          { title: 'ID', dataIndex: 'id', width: 70 },
          { title: '套图', dataIndex: 'bundleTitle' },
          {
            title: '消费者',
            render: (_, r) => r.userNickname || r.userPhone || '匿名',
          },
          { title: '实际扣币', dataIndex: 'purchaseCoins' },
          { title: '已退币', dataIndex: 'refundedCoins' },
          {
            title: '订单状态',
            dataIndex: 'status',
            render: (v) => (
              <Tag color={v === 'partial' ? 'orange' : v === 'failed' ? 'red' : 'default'}>{v}</Tag>
            ),
          },
          {
            title: '创建时间',
            dataIndex: 'createdAt',
            render: (v) => dayjs(v).format('YYYY-MM-DD HH:mm'),
          },
          {
            title: '操作',
            render: (_, row) =>
              row.refundedCoins > 0 || row.status === 'refunded' ? (
                <Tag>已退款</Tag>
              ) : (
                <Button
                  size="small"
                  type="primary"
                  danger
                  onClick={() => {
                    setNoteOpen(row);
                    setNote(`订单 #${row.id} 退款`);
                  }}
                >
                  全额退款
                </Button>
              ),
          },
        ]}
      />

      <Modal
        title={`退款订单 #${noteOpen?.id}`}
        open={!!noteOpen}
        onOk={refund}
        onCancel={() => setNoteOpen(null)}
      >
        <Typography.Paragraph>
          将把 <b>{noteOpen?.purchaseCoins}</b> 币退还给消费者，并同时扣回已计入的创作者分成。
        </Typography.Paragraph>
        <Input.TextArea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
      </Modal>
    </Space>
  );
};

export default PurchaseExceptionsPage;
